import { lazy, Suspense, useEffect, useState, Component, type ReactNode, type ErrorInfo } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { Header } from './components/Header';
import { Footer } from './components/Footer';
import { PageLoader } from './components/PageLoader';
import { ProtectedRoute } from './components/ProtectedRoute';
import { FeatureGate } from './components/FeatureGate';

function isChunkLoadError(error: Error): boolean {
  return (
    error.message.includes('dynamically imported module') ||
    error.message.includes('Loading chunk') ||
    error.message.includes('Failed to fetch') ||
    error.name === 'ChunkLoadError'
  );
}

class ErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null; stack: string }> {
  state = { error: null as Error | null, stack: '' };
  static getDerivedStateFromError(error: Error) { return { error }; }
  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[ErrorBoundary]', error, info.componentStack);
    this.setState({ stack: info.componentStack || '' });

    // Auto-reload once on chunk load failures (stale deploy assets)
    if (isChunkLoadError(error)) {
      const key = 'chunk-reload-attempted';
      if (!sessionStorage.getItem(key)) {
        sessionStorage.setItem(key, '1');
        window.location.reload();
        return;
      }
      // Already tried once this session — clear flag and show error
      sessionStorage.removeItem(key);
    }
  }
  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: '2rem', maxWidth: '700px', margin: '4rem auto', fontFamily: 'monospace' }}>
          <h2 style={{ color: '#dc2626' }}>Something went wrong</h2>
          <pre style={{ whiteSpace: 'pre-wrap', background: '#f1f5f9', padding: '1rem', borderRadius: '0.5rem', fontSize: '0.85rem', maxHeight: '200px', overflow: 'auto' }}>
            {this.state.error.message}
          </pre>
          {this.state.stack && (
            <>
              <h3 style={{ marginTop: '1rem', color: '#64748b' }}>Component Stack</h3>
              <pre style={{ whiteSpace: 'pre-wrap', background: '#fef2f2', padding: '1rem', borderRadius: '0.5rem', fontSize: '0.75rem', maxHeight: '300px', overflow: 'auto' }}>
                {this.state.stack}
              </pre>
            </>
          )}
          <button onClick={() => { this.setState({ error: null, stack: '' }); window.location.href = '/'; }}
            style={{ marginTop: '1rem', padding: '0.5rem 1rem', background: '#2563eb', color: 'white', border: 'none', borderRadius: '0.25rem', cursor: 'pointer' }}>
            Go Home
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
import DynamicPage from './components/DynamicPage';
import Contact from './pages/Contact';
import NotFound from './pages/NotFound';
import Login from './pages/Login';
import Register from './pages/Register';
import VerifyEmail from './pages/VerifyEmail';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import EmailComposer from './pages/admin/EmailComposer';
import Registration from './pages/Registration';
import TermsConditions from './pages/TermsConditions';
import PrivacyPolicy from './pages/PrivacyPolicy';
import CancellationPolicy from './pages/CancellationPolicy';

const Dashboard = lazy(() => import('./pages/Dashboard'));
const Account = lazy(() => import('./pages/Account'));
const ManageRegistration = lazy(() => import('./pages/ManageRegistration'));
const Villas = lazy(() => import('./pages/Villas'));
const AdminVillas = lazy(() => import('./pages/AdminVillas'));
const Payments = lazy(() => import('./pages/Payments'));
const AdminPayments = lazy(() => import('./pages/AdminPayments'));
const Schedule = lazy(() => import('./pages/Schedule'));
const Calendar = lazy(() => import('./pages/Calendar'));
const DayPlan = lazy(() => import('./pages/DayPlan'));
const Polls = lazy(() => import('./pages/Polls'));
const AdminEvents = lazy(() => import('./pages/admin/AdminEvents'));
const AdminCalendar = lazy(() => import('./pages/admin/AdminCalendar'));
const AdminRsvpDashboard = lazy(() => import('./pages/admin/AdminRsvpDashboard'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminMembers = lazy(() => import('./pages/admin/AdminMembers'));
const AdminMemberDetail = lazy(() => import('./pages/admin/AdminMemberDetail'));
const AdminPolls = lazy(() => import('./pages/AdminPolls'));
const Directory = lazy(() => import('./pages/Directory'));
const FamilyUnits = lazy(() => import('./pages/FamilyUnits'));
const Bulletin = lazy(() => import('./pages/Bulletin'));
const BulletinAdmin = lazy(() => import('./pages/admin/BulletinAdmin'));
const AdminSettings = lazy(() => import('./pages/admin/AdminSettings'));
const AdminBudget = lazy(() => import('./pages/admin/AdminBudget'));
const AdminAnalytics = lazy(() => import('./pages/admin/AdminAnalytics'));
const AdminVendors = lazy(() => import('./pages/admin/AdminVendors'));
const AdminCatering = lazy(() => import('./pages/admin/AdminCatering'));
const Inbox = lazy(() => import('./pages/Inbox'));
const MapPage = lazy(() => import('./pages/MapPage'));
const AdminMap = lazy(() => import('./pages/admin/AdminMap'));
const Shop = lazy(() => import('./pages/Shop'));
const AdminShop = lazy(() => import('./pages/admin/AdminShop'));
const Reunion2027 = lazy(() => import('./pages/Reunion2027'));
const PublicPay = lazy(() => import('./pages/PublicPay'));

/**
 * Main Application Component
 * 
 * Provides the core application structure with routing, header, and footer.
 * Implements a hybrid approach supporting both WordPress CMS content and local React components.
 * 
 * @returns {JSX.Element} The main app component with routing configuration
 */
export const App = () => {
  // Application initialization state
  const [isLoading, setIsLoading] = useState(true);
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (!hash) {
      window.scrollTo(0, 0);
    }
  }, [pathname, hash]);

  useEffect(() => {
    setIsLoading(false);
  }, []);

  // Show loading spinner during app initialization
  if (isLoading) {
    return <PageLoader />;
  }

  return (
    <ErrorBoundary>
    <div className="min-h-screen flex flex-col" data-testid="app-container">
      {/* Site header with navigation */}
      <Header />

      {/* Main content area with routing */}
      <main className="flex-grow" data-testid="main-content">
        <Routes>
          {/* Home route - renders DynamicPage with 'home' slug */}
          <Route path="/" element={<DynamicPage />} />

          {/* Contact page route */}
          <Route path="/contact" element={<Contact />} />

          {/* Auth routes (public) */}
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />

          {/* 2027 Reunion reveal page (public, feature-gated inside the component) */}
          <Route path="/reunion-2027" element={
            <Suspense fallback={<PageLoader />}>
              <Reunion2027 />
            </Suspense>
          } />

          {/* Public payment page (no auth; feature-gated inside the component) */}
          <Route path="/pay" element={
            <Suspense fallback={<PageLoader />}>
              <PublicPay />
            </Suspense>
          } />

          {/* Legal/Policy routes (public) */}
          <Route path="/terms" element={<TermsConditions />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/cancellation-policy" element={<CancellationPolicy />} />

          {/* Protected routes */}
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <Suspense fallback={<PageLoader />}>
                <Dashboard />
              </Suspense>
            </ProtectedRoute>
          } />

          <Route path="/account" element={
            <ProtectedRoute>
              <Suspense fallback={<PageLoader />}>
                <Account />
              </Suspense>
            </ProtectedRoute>
          } />

          <Route path="/registration/manage" element={
            <ProtectedRoute>
              <Suspense fallback={<PageLoader />}>
                <ManageRegistration />
              </Suspense>
            </ProtectedRoute>
          } />

          <Route path="/registration" element={
            <ProtectedRoute>
              <Registration />
            </ProtectedRoute>
          } />

          <Route path="/payments" element={
            <ProtectedRoute>
              <FeatureGate feature="paymentsEnabled">
                <Suspense fallback={<PageLoader />}>
                  <Payments />
                </Suspense>
              </FeatureGate>
            </ProtectedRoute>
          } />

          <Route path="/villas" element={
            <ProtectedRoute>
              <FeatureGate feature="villasEnabled">
                <Suspense fallback={<PageLoader />}>
                  <Villas />
                </Suspense>
              </FeatureGate>
            </ProtectedRoute>
          } />

          <Route path="/schedule" element={
            <ProtectedRoute>
              <FeatureGate feature="calendarEnabled">
                <Suspense fallback={<PageLoader />}>
                  <Schedule />
                </Suspense>
              </FeatureGate>
            </ProtectedRoute>
          } />

          <Route path="/calendar" element={
            <ProtectedRoute>
              <FeatureGate feature="calendarEnabled">
                <Suspense fallback={<PageLoader />}>
                  <Calendar />
                </Suspense>
              </FeatureGate>
            </ProtectedRoute>
          } />

          <Route path="/calendar/day/:date" element={
            <ProtectedRoute>
              <FeatureGate feature="calendarEnabled">
                <Suspense fallback={<PageLoader />}>
                  <DayPlan />
                </Suspense>
              </FeatureGate>
            </ProtectedRoute>
          } />

          <Route path="/polls" element={
            <ProtectedRoute>
              <FeatureGate feature="pollsEnabled">
                <Suspense fallback={<PageLoader />}>
                  <Polls />
                </Suspense>
              </FeatureGate>
            </ProtectedRoute>
          } />

          <Route path="/directory" element={
            <ProtectedRoute>
              <FeatureGate feature="directoryEnabled">
                <Suspense fallback={<PageLoader />}>
                  <Directory />
                </Suspense>
              </FeatureGate>
            </ProtectedRoute>
          } />

          <Route path="/family-units" element={
            <ProtectedRoute>
              <Suspense fallback={<PageLoader />}>
                <FamilyUnits />
              </Suspense>
            </ProtectedRoute>
          } />

          <Route path="/shop" element={
            <ProtectedRoute>
              <FeatureGate feature="shopEnabled">
                <Suspense fallback={<PageLoader />}>
                  <Shop />
                </Suspense>
              </FeatureGate>
            </ProtectedRoute>
          } />

          <Route path="/bulletin" element={
            <ProtectedRoute>
              <FeatureGate feature="bulletinEnabled">
                <Suspense fallback={<PageLoader />}>
                  <Bulletin />
                </Suspense>
              </FeatureGate>
            </ProtectedRoute>
          } />

          <Route path="/inbox" element={
            <ProtectedRoute>
              <Suspense fallback={<PageLoader />}>
                <Inbox />
              </Suspense>
            </ProtectedRoute>
          } />

          <Route path="/map" element={
            <ProtectedRoute>
              <FeatureGate feature="mapEnabled">
                <Suspense fallback={<PageLoader />}>
                  <MapPage />
                </Suspense>
              </FeatureGate>
            </ProtectedRoute>
          } />

          {/* Admin routes */}
          <Route path="/admin" element={
            <ProtectedRoute requiredRole="reunion_admin">
              <Suspense fallback={<PageLoader />}>
                <AdminDashboard />
              </Suspense>
            </ProtectedRoute>
          } />

          <Route path="/admin/members" element={
            <ProtectedRoute requiredRole="reunion_admin">
              <Suspense fallback={<PageLoader />}>
                <AdminMembers />
              </Suspense>
            </ProtectedRoute>
          } />

          <Route path="/admin/members/:id" element={
            <ProtectedRoute requiredRole="reunion_admin">
              <Suspense fallback={<PageLoader />}>
                <AdminMemberDetail />
              </Suspense>
            </ProtectedRoute>
          } />

          <Route path="/admin/villas" element={
            <ProtectedRoute requiredRole="reunion_admin">
              <Suspense fallback={<PageLoader />}>
                <AdminVillas />
              </Suspense>
            </ProtectedRoute>
          } />

          <Route path="/admin/payments" element={
            <ProtectedRoute requiredRole="reunion_admin">
              <Suspense fallback={<PageLoader />}>
                <AdminPayments />
              </Suspense>
            </ProtectedRoute>
          } />

          <Route path="/admin/events" element={
            <ProtectedRoute requiredRole="reunion_admin">
              <Suspense fallback={<PageLoader />}>
                <AdminEvents />
              </Suspense>
            </ProtectedRoute>
          } />

          <Route path="/admin/calendar" element={
            <ProtectedRoute requiredRole="reunion_admin">
              <Suspense fallback={<PageLoader />}>
                <AdminCalendar />
              </Suspense>
            </ProtectedRoute>
          } />

          <Route path="/admin/rsvps" element={
            <ProtectedRoute requiredRole="reunion_admin">
              <Suspense fallback={<PageLoader />}>
                <AdminRsvpDashboard />
              </Suspense>
            </ProtectedRoute>
          } />

          <Route path="/admin/polls" element={
            <ProtectedRoute requiredRole="reunion_admin">
              <Suspense fallback={<PageLoader />}>
                <AdminPolls />
              </Suspense>
            </ProtectedRoute>
          } />

          <Route path="/admin/bulletin" element={
            <ProtectedRoute requiredRole="reunion_admin">
              <Suspense fallback={<PageLoader />}>
                <BulletinAdmin />
              </Suspense>
            </ProtectedRoute>
          } />

          <Route path="/admin/email" element={
            <ProtectedRoute requiredRole="reunion_admin">
              <EmailComposer />
            </ProtectedRoute>
          } />

          <Route path="/admin/budget" element={
            <ProtectedRoute requiredRole="reunion_admin">
              <Suspense fallback={<PageLoader />}>
                <AdminBudget />
              </Suspense>
            </ProtectedRoute>
          } />

          <Route path="/admin/vendors" element={
            <ProtectedRoute requiredRole="reunion_admin">
              <Suspense fallback={<PageLoader />}>
                <AdminVendors />
              </Suspense>
            </ProtectedRoute>
          } />

          <Route path="/admin/catering" element={
            <ProtectedRoute requiredRole="reunion_admin">
              <Suspense fallback={<PageLoader />}>
                <AdminCatering />
              </Suspense>
            </ProtectedRoute>
          } />

          <Route path="/admin/map" element={
            <ProtectedRoute requiredRole="reunion_admin">
              <Suspense fallback={<PageLoader />}>
                <AdminMap />
              </Suspense>
            </ProtectedRoute>
          } />

          <Route path="/admin/analytics" element={
            <ProtectedRoute requiredRole="reunion_admin">
              <Suspense fallback={<PageLoader />}>
                <AdminAnalytics />
              </Suspense>
            </ProtectedRoute>
          } />

          <Route path="/admin/settings" element={
            <ProtectedRoute requiredRole="reunion_admin">
              <Suspense fallback={<PageLoader />}>
                <AdminSettings />
              </Suspense>
            </ProtectedRoute>
          } />

          <Route path="/admin/shop" element={
            <ProtectedRoute requiredRole="reunion_admin">
              <Suspense fallback={<PageLoader />}>
                <AdminShop />
              </Suspense>
            </ProtectedRoute>
          } />

          {/* Not found route */}
          <Route path="/not-found" element={<NotFound />} />

          {/* Dynamic page route - handles WordPress pages and local components */}
          <Route path="/:slug" element={<DynamicPage />} />
        </Routes>
      </main>
      
      {/* Site footer */}
      <Footer />
    </div>
    </ErrorBoundary>
  );
}; 